import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useTranslation } from "react-i18next";
import { updateReport } from "../api/report.api";
import {
  Report,
  ReportStatus,
  UpdateReportPayload,
} from "../api/models/report.models";

const useUpdateReportStatus = ({ companyId }: { companyId: number }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef]),
  );

  const returnReportForEditing = (report: Report) => {
    const payload: UpdateReportPayload = {
      ...report,
      reportStatus: ReportStatus.Draft,
    };
    return updateReportStatusMutation.mutateAsync(payload);
  };

  const updateReportStatusMutation = useMutation({
    mutationFn: (payload: UpdateReportPayload) =>
      updateReport(companyId, payload, bearerToken as string),
    onError: (error) => {
      console.error("Failed to update report status:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-report-update-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("label-report-returned-for-editing"),
        life: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["reports", companyId] });
      queryClient.invalidateQueries({
        queryKey: ["companyReports", companyId],
      });
    },
  });

  return {
    returnReportForEditing,
    isUpdating: updateReportStatusMutation.isPending,
  };
};

export default useUpdateReportStatus;
